import { CheckWithPrefixResponse } from 'lib/infomation_vender/document';
import { memberActionTypes } from 'lib/store/member/reducer';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import Img from 'react-cloudimage-responsive';
import { connect } from 'react-redux';
import { AppReducerStateProps, store } from '../../lib/store';

// Payment V2 API imports
import DOMPurify from 'dompurify';
import { promotionsActionTypes } from 'lib/store/promotions/reducer';
import API, { GetBankTypeAllResponse } from 'lib/worker/api/get_bank_type_all';
import get_exchange_rate_currency, {
  ExchangeRateCurrencyResponse,
} from 'lib/worker/api/get_exchange_rate_currency';
import { BankByMember } from 'lib/worker/api/getinfo';
import paymentV2CancelDeposit, {
  CancelDepositRequest,
} from 'lib/worker/api/payment_v2_cancel_deposit';
import paymentV2GetPendingDeposit, {
  GetPendingDepositResponse,
} from 'lib/worker/api/payment_v2_get_pending_deposit';
import paymentV2GetSlip, { GetSlipAPIResponse } from 'lib/worker/api/payment_v2_get_slip';
import paymentV2NotifyDeposit, {
  NotifyDepositRequest,
} from 'lib/worker/api/payment_v2_notify_deposit';
import paymentV2UploadSlip, { UploadSlipRequest } from 'lib/worker/api/payment_v2_upload_slip';
import QRCode from 'react-qr-code';
import { alertWithMessage } from './message_modal';
import { alertWithSuccessMessage } from './success_modal';

interface AlertWithDepositV2Props {
  state: AppReducerStateProps;
  infomation: CheckWithPrefixResponse;
  closeModal: Function;
}

export const alertWithDepositV2 = () => {
  store.dispatch({
    type: memberActionTypes.ACTION_DEPOSIT_V2_IS_OPEN_MODAL,
  });
};

export const alertWithDepositV2Close = () => {
  store.dispatch({
    type: memberActionTypes.ACTION_DEPOSIT_V2_IS_CLOSE_MODAL,
  });

  store.dispatch({
    type: promotionsActionTypes.ACTION_CLEAR_SELECTED_PROMOTION,
  });
};

function AlertWithDepositV2(props: AlertWithDepositV2Props) {
  const t = (text: string) => text;
  const { isDepositV2Modal } = props.state.member;
  const { closeModal } = props;
  const { getinfo } = props.state.member;
  const { Banks, CurrencyName } = getinfo;
  // State management
  const [step, setStep] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedBank, setSelectedBank] = useState<any>(null);
  const [amount, setAmount] = useState<string>('');
  const [pendingDeposit, setPendingDeposit] = useState<GetPendingDepositResponse | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [bankTypes, setBankTypes] = useState<GetBankTypeAllResponse[]>([]);
  const [exchangeV2, setExchangeV2] = useState<ExchangeRateCurrencyResponse | null>(null);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState<boolean>(false);
  const [showCopyAnimation, setShowCopyAnimation] = useState<boolean>(false);
  const [slipFile, setSlipFile] = useState<File | null>(null);
  const [slipPreview, setSlipPreview] = useState<string>('');
  const [uploadingSlip, setUploadingSlip] = useState<boolean>(false);
  const [uploadedSlipUrl, setUploadedSlipUrl] = useState<string>('');
  const [cancelCooldown, setCancelCooldown] = useState<number>(0);
  const [isDesktopViewport, setIsDesktopViewport] = useState<boolean>(false);
  const { selectedPromotion } = props.state.promotions;

  // Predefined amounts
  const predefinedAmounts = React.useMemo(() => {
    if (exchangeV2 && exchangeV2.rate_in > 0 && exchangeV2.rate_in < 0.01) {
      return [50000, 100000, 300000, 500000, 1000000, 2000000, 3000000, 5000000, 10000000];
    }
    return [100, 200, 300, 500, 800, 1000, 2000, 5000, 10000];
  }, [exchangeV2]);

  // Reset modal when opening and check for pending deposits
  useEffect(() => {
    if (isDepositV2Modal) {
      setSelectedBank(null);
      setAmount('');
      setPendingDeposit(null);
      setTimeLeft(0);
      setLoading(false);
      setUploadedSlipUrl(''); // Reset uploaded slip URL
      setExchangeV2(null);

      get_exchange_rate_currency()
        .then((res) => {
          setExchangeV2(res);
        })
        .catch((err) => {
          console.error(err);
        });

      // Load bank types for destination bank info !
      loadBankTypes();

      // Check for pending deposits and load slip if exists (reload every time modal opens)
      checkPendingDeposits();
    }
  }, [isDepositV2Modal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first bank in first step if banks exist
  useEffect(() => {
    if (Banks && Banks.length > 0 && !selectedBank && step === 1) {
      setSelectedBank(Banks[0]);
    }
  }, [Banks, selectedBank, step]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateViewport = () => {
      setIsDesktopViewport(window.innerWidth >= 640);
    };

    updateViewport();
    window.addEventListener('resize', updateViewport);

    return () => {
      window.removeEventListener('resize', updateViewport);
    };
  }, []);

  // Load bank types
  const loadBankTypes = async () => {
    try {
      const banks = await API();
      setBankTypes(banks);
    } catch (error) {
      console.error('Load bank types error:', error);
    }
  };

  // Find destination bank info from bank types
  const getDestinationBankInfo = () => {
    if (!pendingDeposit || !bankTypes.length) return null;

    const bankName = pendingDeposit.dest_bank_name || pendingDeposit.bank_name;
    if (!bankName) return null;

    // Try to find exact match
    let bankInfo = bankTypes.find((bank) => bank.Type?.toLowerCase() === bankName.toLowerCase());

    // If no exact match, try partial match
    if (!bankInfo) {
      bankInfo = bankTypes.find(
        (bank) =>
          bank.Type?.toLowerCase().includes(bankName.toLowerCase()) ||
          bankName.toLowerCase().includes(bank.Type?.toLowerCase() || '')
      );
    }

    return bankInfo;
  };

  // Handle copy account number with feedback
  const handleCopyAccountNumber = async () => {
    try {
      const accountNumber = pendingDeposit?.dest_bank_account || pendingDeposit?.bank_account || '';

      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(accountNumber);
        setCopySuccess(true);

        // Trigger copy animation
        setShowCopyAnimation(true);
        setTimeout(() => {
          setShowCopyAnimation(false);
        }, 15000);
      } else {
        // Fallback for older browsers and mobile devices
        const textArea = document.createElement('textarea');
        textArea.value = accountNumber;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          document.execCommand('copy');
          setCopySuccess(true);

          // Trigger copy animation
          setShowCopyAnimation(true);
          setTimeout(() => {
            setShowCopyAnimation(false);
          }, 15000);
        } catch (err) {
          console.error('Fallback copy failed:', err);
          // Show manual copy instructions for mobile
          alertWithMessage(`${t('คัดลอกเลขบัญชี')}: ${accountNumber}`);
        }

        document.body.removeChild(textArea);
      }

      // Reset after 2 seconds
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Copy failed:', error);
      // Show manual copy instructions as last resort
      const accountNumber = pendingDeposit?.dest_bank_account || pendingDeposit?.bank_account || '';
      alertWithMessage(`${t('คัดลอกเลขบัญชี')}: ${accountNumber}`);
    }
  };

  // Handle copy reference ID with feedback
  const handleCopyReferenceId = async () => {
    try {
      const referenceId = pendingDeposit?.reference_id || '';

      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(referenceId);
        alertWithMessage(t('คัดลอกหมายเลขอ้างอิงแล้ว'));
      } else {
        // Fallback for older browsers and mobile devices
        const textArea = document.createElement('textarea');
        textArea.value = referenceId;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          document.execCommand('copy');
          alertWithSuccessMessage(t('คัดลอกหมายเลขอ้างอิงแล้ว'));
        } catch (err) {
          console.error('Fallback copy failed:', err);
          // Show manual copy instructions for mobile
          alertWithMessage(`${t('คัดลอกหมายเลขอ้างอิง')}: ${referenceId}`);
        }

        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error('Copy reference ID failed:', error);
      // Show manual copy instructions as last resort
      const referenceId = pendingDeposit?.reference_id || '';
      alertWithMessage(`${t('คัดลอกหมายเลขอ้างอิง')}: ${referenceId}`);
    }
  };

  // Handle slip file selection
  const handleSlipFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alertWithMessage(t('กรุณาอัพโหลดไฟล์รูปภาพเท่านั้น'));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alertWithMessage(t('ขนาดไฟล์ต้องไม่เกิน 5MB'));
        return;
      }

      setSlipFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setSlipPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle remove slip
  const handleRemoveSlip = () => {
    setSlipFile(null);
    setSlipPreview('');
  };

  // Handle upload slip
  const handleUploadSlip = async () => {
    if (!slipFile) {
      alertWithMessage(t('กรุณาเลือกไฟล์สลิป'));
      return;
    }

    if (!pendingDeposit?.reference_id) {
      alertWithMessage(t('ไม่พบหมายเลขอ้างอิง'));
      return;
    }

    setUploadingSlip(true);
    try {
      const uploadRequest: UploadSlipRequest = {
        reference_id: pendingDeposit.reference_id,
        file: slipFile,
      };

      const response = await paymentV2UploadSlip(uploadRequest);

      if (response.success) {
        alertWithSuccessMessage(response.message || t('อัพโหลดสลิปสำเร็จ'));

        // Clear slip file and preview after successful upload
        setSlipFile(null);
        setSlipPreview('');

        // Fetch and display the uploaded slip
        try {
          const slipData: GetSlipAPIResponse = await paymentV2GetSlip(pendingDeposit.reference_id);
          if (slipData.success && slipData.data.slip_url) {
            setUploadedSlipUrl(slipData.data.slip_url);
          }
        } catch (error) {
          console.error('Failed to fetch slip:', error);
        }

        // Optional: refresh pending deposit data to get updated slip URL
        await loadPendingDeposit();
      } else {
        throw new Error(response.message || t('ไม่สามารถอัพโหลดสลิปได้'));
      }
    } catch (error: any) {
      console.error('Upload slip error:', error);
      const errorMessage =
        error.response?.data?.message || error.message || t('เกิดข้อผิดพลาดในการอัพโหลดสลิป');
      alertWithMessage(errorMessage);
    } finally {
      setUploadingSlip(false);
    }
  };

  // Check if can cancel (must be more than 5 seconds since created)
  const canCancelDeposit = () => {
    if (!pendingDeposit?.created_at) {
      return false;
    }

    const createdTime = moment(pendingDeposit.created_at);
    const now = moment();
    const diffSeconds = now.diff(createdTime, "seconds");
    return diffSeconds >= 5;
  };

  const getCancelCooldownSeconds = () => {
    if (!pendingDeposit?.created_at) return 0;
    const createdTime = moment(pendingDeposit.created_at);
    const now = moment();
    const diffSeconds = now.diff(createdTime, "seconds");
    return Math.max(0, 5 - diffSeconds);
  };

  // Handle cancel deposit with confirmation
  const handleCancelDeposit = () => {
    if (!canCancelDeposit()) {
      alertWithMessage(t('ไม่สามารถยกเลิกได้ กรุณารออย่างน้อย 5 วินาทีหลังจากทำรายการ'));
      return;
    }

    setShowCancelConfirm(true);
  };

  // Confirm cancel deposit
  const confirmCancelDeposit = async () => {
    try {
      setLoading(true);
      setShowCancelConfirm(false);

      if (!pendingDeposit?.transaction_id) {
        throw new Error('ไม่พบ transaction_id');
      }

      const cancelRequest: CancelDepositRequest = {
        transaction_id: pendingDeposit.transaction_id,
        note: t('ผู้ใช้ยกเลิกรายการ'),
      };

      const result = await paymentV2CancelDeposit(cancelRequest);

      if (result.success) {
        // Reset states and go back to step 1
        setPendingDeposit(null);
        setTimeLeft(0);
        setStep(1);

        alertWithSuccessMessage(t('ยกเลิกรายการสำเร็จ'));
      } else {
        throw new Error(result.message || t('ไม่สามารถยกเลิกรายการได้'));
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || t('เกิดข้อผิดพลาด');
      alertWithMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Check for pending deposits when modal opens
  const checkPendingDeposits = async () => {
    try {
      const pending = await paymentV2GetPendingDeposit();
      if (pending && pending.reference_id) {
        // Has pending deposit, go to step 3
        setPendingDeposit(pending);

        // Auto-select bank based on pending deposit by matching bank info
        if (Banks && Banks.length > 0) {
          // Try to match by bank account number first (most reliable)
          let matchingBank = null;

          // Method 1: Match by account number in reference_id or any available field
          if (pending.reference_id) {
            matchingBank = Banks.find(
              (bank) =>
                pending.reference_id?.includes(bank.BankNumber || '') ||
                bank.BankNumber?.includes(pending.reference_id || '')
            );
          }

          // Method 2: If no match by account, try matching by bank name
          if (!matchingBank && pending.bank_name) {
            matchingBank = Banks.find(
              (bank) =>
                bank.Type?.toLowerCase().includes(pending.bank_name?.toLowerCase() || '') ||
                pending.bank_name?.toLowerCase().includes(bank.Type?.toLowerCase() || '')
            );
          }

          // Method 3: If still no match, use the first bank as fallback
          if (!matchingBank && Banks.length > 0) {
            matchingBank = Banks[0];
          }

          if (matchingBank) {
            setSelectedBank(matchingBank);
          }
        }

        // Set amount from pending deposit
        if (pending.amount) {
          setAmount(pending.amount.toString());
        }

        // Calculate time left (30 minutes from created_at)
        if (pending.created_at) {
          const createdTime = moment(pending.created_at);
          const now = moment();
          const targetTime = createdTime.add(30, 'minutes');
          const diffSeconds = targetTime.diff(now, 'seconds');
          setTimeLeft(Math.max(0, diffSeconds));
        }

        // Try to load existing slip if available
        if (pending.reference_id) {
          try {
            const slipData: GetSlipAPIResponse = await paymentV2GetSlip(pending.reference_id);
            if (slipData.success && slipData.data.slip_url) {
              setUploadedSlipUrl(slipData.data.slip_url);
            }
          } catch (error) {
            console.error('Failed to fetch existing slip:', error);
            // Not a critical error, just means no slip uploaded yet
          }
        }

        setStep(3);
      } else {
        // No pending deposit, start from step 1
        setStep(1);
      }
    } catch (error) {
      // If error, start from step 1
      setStep(1);
    }
  };

  // Timer for step 3
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (step === 3 && pendingDeposit?.created_at && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [step, pendingDeposit, timeLeft]);

  // Cancel cooldown timer (5 seconds after created_at)
  useEffect(() => {
    if (step !== 3 || !pendingDeposit?.created_at) {
      setCancelCooldown(0);
      return;
    }

    const updateCooldown = () => {
      setCancelCooldown(getCancelCooldownSeconds());
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);
    return () => clearInterval(interval);
  }, [step, pendingDeposit?.created_at]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading && isDepositV2Modal) {
        closeModal();
      }
    };

    if (isDepositV2Modal) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isDepositV2Modal, loading, closeModal]);

  const stepTitles: { [key: number]: string } = {
    1: t('เลือกบัญชีของฉัน'),
    2: t('ระบุจำนวนเงิน'),
    3: t('ยืนยันการโอนเงิน'),
  };

  const handleNext = () => {
    if (step === 1 && selectedBank) {
      setStep(2);
    } else if (step === 2) {
      handleNotifyDeposit();
    } else if (step === 3) {
      // ในขั้นตอนที่ 3 ปุ่ม "ทำรายการใหม่" จะเรียกฟังก์ชันยกเลิก
      handleCancelDeposit();
    }
  };

  const getButtonText = () => {
    if (step === 1) return t('ถัดไป');
    if (step === 2) return loading ? t('กำลังดำเนินการ...') : t('แจ้งฝากเงิน');
    if (step === 3) {
      if (cancelCooldown > 0) return `${t('รอ')} ${cancelCooldown} ${t('วินาที')}`;
      return t('ทำรายการใหม่');
    }
    return t('ถัดไป');
  };

  const isButtonDisabled = () => {
    if (step === 1) return !selectedBank || loading;
    if (step === 2) return !amount || parseFloat(amount) < 1 || loading;
    if (step === 3) return loading || cancelCooldown > 0;
    return loading;
  };

  // Handle amount selection
  const handleAmountSelect = (selectedAmount: number) => {
    setAmount(selectedAmount.toString());
  };

  // Handle notify deposit
  const handleNotifyDeposit = async () => {
    setLoading(true);
    try {
      const request: NotifyDepositRequest = {
        bank_member_id: selectedBank.ID,
        promotion_type_id: selectedPromotion?.ID, // Default no promotion
        amount: parseFloat(amount),
      };

      const response = await paymentV2NotifyDeposit(request);

      if (response.success) {
        // Move to step 3 and get pending deposit
        await loadPendingDeposit();
        setStep(3);
      } else {
        alert(response.message || t('เกิดข้อผิดพลาด'));
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || t('เกิดข้อผิดพลาด');
      alertWithMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Load pending deposit
  const loadPendingDeposit = async () => {
    try {
      const pending = await paymentV2GetPendingDeposit();
      setPendingDeposit(pending);

      // Calculate time left (30 minutes from created_at)
      if (pending.created_at) {
        const createdTime = moment(pending.created_at);
        const now = moment();
        const targetTime = createdTime.add(30, 'minutes');
        const diffSeconds = targetTime.diff(now, 'seconds');
        setTimeLeft(Math.max(0, diffSeconds));
      }
    } catch (error) {
      console.error('Load pending deposit error:', error);
    }
  };

  // Format time left
  const formatTimeLeft = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Render selected promotion info
  const renderSelectedPromotionInfo = () => {
    if (!selectedPromotion) return null;

    return (
      <div className="mt-3 p-2 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200">
        <div className="flex items-center space-x-2 mb-1">
          <i className="fas fa-info-circle text-blue-600 text-xs"></i>
          <span className="text-blue-800 font-semibold text-xs">{t('โปรโมชั่นที่เลือก')}</span>
        </div>

        <div className="bg-white/80 rounded-md p-2 text-xs">
          <div className="flex items-start space-x-2 grid grid-cols-2">
            {selectedPromotion.Image && (
              <Img
                src={selectedPromotion.Image}
                alt={selectedPromotion.Type || 'Promotion'}
                className="w-5 h-5 rounded-md shadow-sm border object-cover flex-shrink-0"
                ratio={1 / 1.5}
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-900 text-xs mb-1 leading-tight">
                {selectedPromotion.Type || t('โปรโมชั่นพิเศษ')}
              </p>
              <div className="text-xs text-gray-600 space-y-0.5">
                {selectedPromotion.Amount && (
                  <div className="flex items-center space-x-1">
                    <span className="font-medium">{t('จำนวน')}:</span>
                    <span>
                      {selectedPromotion.Amount?.toLocaleString()}{' '}
                      {CurrencyName === 'THB' ? '' : CurrencyName || ''}
                    </span>
                  </div>
                )}
                {selectedPromotion.Higher && (
                  <div className="flex items-center space-x-1">
                    <span className="font-medium">{t('สูงสุด')}:</span>
                    <span>
                      {selectedPromotion.Higher?.toLocaleString()}{' '}
                      {CurrencyName === 'THB' ? '' : CurrencyName || ''}
                    </span>
                  </div>
                )}
                {selectedPromotion.Lower !== undefined && (
                  <div className="flex items-center space-x-1">
                    <span className="font-medium">{t('ขั้นต่ำ')}:</span>
                    <span>
                      {selectedPromotion.Lower?.toLocaleString()}{' '}
                      {CurrencyName === 'THB' ? '' : CurrencyName || ''}
                    </span>
                  </div>
                )}
                {selectedPromotion.Content && (
                  <div className="flex items-start space-x-1">
                    <span className="font-medium">{t('เงื่อนไข')}:</span>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(selectedPromotion.Content),
                      }}
                    ></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Step 1: Bank Selection
  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="text-center">
        <div className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-blue-50 to-blue-100 rounded-full mb-3">
          <i className="fas fa-university text-blue-600 mr-2 text-sm"></i>
          <span className="text-gray-900 font-semibold text-xs">{t('เลือกบัญชีธนาคาร')}</span>
        </div>
        <p className="text-gray-600 text-xs leading-relaxed">
          {t('โปรดเลือกบัญชีที่จะใช้สำหรับการฝากเงิน')}
        </p>
      </div>

      {Banks && Banks.length > 0 ? (
        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
          {Banks.map((bank: BankByMember) => (
            <div
              key={bank.ID}
              className={`group relative rounded-xl border-2 p-3 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                selectedBank?.ID === bank.ID
                  ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-blue-100 shadow-md'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
              }`}
              onClick={() => setSelectedBank(bank)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden transition-all duration-300 shadow-md ${
                      selectedBank?.ID === bank.ID
                        ? 'ring-2 ring-blue-400'
                        : 'hover:ring-2 hover:ring-blue-200'
                    }`}
                    style={{
                      backgroundColor:
                        bank.Color ||
                        (selectedBank?.ID === bank.ID
                          ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)'
                          : 'linear-gradient(135deg, #f3f4f6, #e5e7eb)'),
                    }}
                  >
                    {bank.Image ? (
                      <Img
                        src={bank.Image}
                        alt={bank.Type}
                        className="w-8 h-8 object-contain"
                        ratio={1}
                      />
                    ) : (
                      <i
                        className={`fas fa-building text-base ${
                          selectedBank?.ID === bank.ID ? 'text-white' : 'text-gray-600'
                        }`}
                      ></i>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h6 className="font-bold text-gray-900 text-sm mb-0.5 truncate">{bank.Type}</h6>
                    <p className="text-xs font-mono text-gray-700 mb-0.5 tracking-wider">
                      {bank.BankNumber}
                    </p>
                    {bank.BankName && (
                      <p className="text-xs text-gray-500 truncate">{bank.BankName}</p>
                    )}
                  </div>
                </div>
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                    selectedBank?.ID === bank.ID
                      ? 'border-blue-500 bg-blue-500 scale-110'
                      : 'border-gray-300 group-hover:border-blue-400'
                  }`}
                >
                  {selectedBank?.ID === bank.ID && (
                    <i className="fas fa-check text-white text-xs"></i>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="w-14 h-14 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-md">
            <i className="fas fa-exclamation-triangle text-yellow-600 text-lg"></i>
          </div>
          <h6 className="font-bold text-gray-900 text-base mb-1">{t('ไม่พบข้อมูลธนาคาร')}</h6>
          <p className="text-gray-600 text-xs leading-relaxed max-w-xs mx-auto">
            {t('กรุณาติดต่อฝ่ายบริการลูกค้า')}
          </p>
        </div>
      )}

      {renderSelectedPromotionInfo()}
    </div>
  );

  // Render Step 2: Amount Input
  const renderStep2 = () => (
    <div className="space-y-3 sm:space-y-4">
      {/* Selected bank info */}
      {selectedBank && (
        <div className="bg-gradient-to-r from-white to-gray-50 rounded-xl border-2 border-gray-200 p-3 shadow-md">
          <div className="flex items-center space-x-3">
            {/* Bank Icon */}
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg overflow-hidden flex-shrink-0"
              style={{
                backgroundColor: selectedBank.Color || '#6b7280',
              }}
            >
              {selectedBank.Image ? (
                <Img
                  src={selectedBank.Image}
                  alt={selectedBank.Type}
                  className="w-7 h-7 object-contain"
                  ratio={1}
                />
              ) : (
                <i className="fas fa-building text-white text-sm"></i>
              )}
            </div>

            {/* Bank Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <div className="inline-flex items-center px-2 py-0.5 bg-gradient-to-r from-green-50 to-green-100 rounded-full">
                  <i className="fas fa-check-circle text-green-600 mr-1 text-xs"></i>
                </div>
              </div>

              <div className="space-y-0.5">
                <h3 className="font-bold text-gray-900 text-base truncate">{selectedBank.Type}</h3>
                <p className="text-sm font-mono text-gray-700 tracking-wider truncate">
                  {selectedBank.BankNumber}
                </p>
                {selectedBank.BankName && (
                  <p className="text-sm text-gray-600 truncate">{selectedBank.BankName}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <label className="block text-gray-900 font-bold text-sm sm:text-base">
          {t('จำนวนเงิน')}
        </label>
        <div className="relative">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-bold text-base sm:text-lg">
            {CurrencyName === 'THB' ? '' : CurrencyName || ''}
          </div>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full pl-8 pr-3 py-3 sm:py-4 text-lg sm:text-xl font-bold text-gray-900 bg-gradient-to-r from-white to-gray-50 border-2 border-gray-300 rounded-lg sm:rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all duration-300 shadow-sm"
            placeholder="0"
            min="1"
          />
        </div>
        {exchangeV2 && exchangeV2.rate_in > 0 && (
          <div className="flex flex-col items-end gap-1 px-1">
            <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-md border border-blue-100 shadow-sm">
              1 {CurrencyName === 'THB' ? '' : CurrencyName || ''} = {exchangeV2.rate_in} Credit
            </span>
            {amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0 && (
              <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-md border border-green-100 shadow-sm animate-in fade-in slide-in-from-top-1 duration-200">
                {t('ได้รับ')}{' '}
                {(parseFloat(amount) * exchangeV2.rate_in).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                Credit
              </span>
            )}
          </div>
        )}
      </div>

      {/* Predefined amounts */}
      <div className="space-y-2">
        <label className="block text-gray-900 font-bold text-sm sm:text-base">
          {t('เลือกจำนวนเงิน')}
        </label>
        <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
          {predefinedAmounts.map((preAmount) => (
            <button
              key={preAmount}
              className={`py-2 sm:py-3 px-1.5 sm:px-2 rounded-md sm:rounded-lg border-2 transition-all duration-300 font-bold text-xs transform hover:scale-105 active:scale-95 ${
                amount === preAmount.toString()
                  ? 'border-green-500 bg-green-500 text-white shadow-md'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-500 hover:bg-gray-50 shadow-sm'
              }`}
              onClick={() => handleAmountSelect(preAmount)}
            >
              {CurrencyName === 'THB' ? '' : CurrencyName || ''}
              {preAmount.toLocaleString()}
            </button>
          ))}
        </div>
      </div>

      {renderSelectedPromotionInfo()}
    </div>
  );

  // Render Step 3: Pending Deposit Info
  const renderStep3versionBank = () => (
    <div className="space-y-4">
      {pendingDeposit ? (
        <div>
          {/* Transfer Flow */}
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 border-gray-200 p-3 sm:p-4 shadow-md">
            <div className="text-center mb-3 sm:mb-4">
              <h6 className="font-bold text-gray-900 text-sm sm:text-base mb-1 sm:mb-2">
                {t('ข้อมูลการโอนเงิน')}
              </h6>
              <div className="w-12 sm:w-16 h-0.5 bg-gradient-to-r from-blue-400 to-blue-600 mx-auto rounded-full"></div>
            </div>

            {/* 2 Column Layout */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4 relative overflow-hidden">
              {/* Source Bank (Left) */}
              <div
                className={`bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border-2 border-blue-200 p-2.5 shadow-sm transition-all duration-700 ${
                  showCopyAnimation
                    ? 'scale-75 opacity-20 -translate-x-8'
                    : 'scale-100 opacity-100 translate-x-0'
                }`}
              >
                <div className="text-center">
                  {(() => {
                    // Find source bank info from Banks array using the selected bank ID from pendingDeposit
                    const sourceBankInfo =
                      Banks?.find((bank) => bank.ID === selectedBank?.ID) || selectedBank;
                    return (
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-1.5 shadow-md overflow-hidden"
                        style={{
                          backgroundColor: sourceBankInfo?.Color || '#3b82f6',
                        }}
                      >
                        {sourceBankInfo?.Image ? (
                          <Img
                            src={sourceBankInfo.Image}
                            alt={sourceBankInfo.Type}
                            className="w-6 h-6 object-contain"
                            ratio={1}
                          />
                        ) : (
                          <i className="fas fa-user text-white text-sm"></i>
                        )}
                      </div>
                    );
                  })()}
                  <h6 className="font-bold text-gray-900 mb-1.5 text-xs">{t('จาก')}</h6>

                  {/* Bank Name */}
                  <div className="mb-1.5">
                    <p className="text-gray-600 text-xs mb-0.5">{t('ธนาคาร')}</p>
                    <p className="font-bold text-gray-900 text-xs leading-tight">
                      {selectedBank?.Type || t('ธนาคารของคุณ')}
                    </p>
                  </div>

                  {/* Account Number */}
                  <div className="mb-1.5">
                    <p className="text-gray-600 text-xs mb-0.5">{t('เลขบัญชี')}</p>
                    <div className="bg-white/80 backdrop-blur-sm rounded p-1.5 shadow-sm">
                      <code className="text-gray-900 text-xs font-mono tracking-tight break-all leading-tight">
                        {selectedBank?.BankNumber || 'XXX-X-XXXXX-X'}
                      </code>
                    </div>
                  </div>

                  {/* Account Name */}
                  {selectedBank?.BankName && (
                    <div>
                      <p className="text-gray-600 text-xs mb-0.5">{t('ชื่อบัญชี')}</p>
                      <p className="text-gray-900 text-xs font-medium leading-tight">
                        {selectedBank.BankName}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Destination Bank (Right) */}
              <div
                className={`bg-gradient-to-br from-green-50 to-green-100 rounded-xl border-2 border-green-200 p-2.5 shadow-sm transition-all duration-700 ${
                  showCopyAnimation
                    ? 'absolute inset-0 z-10 scale-105 shadow-2xl ring-4 ring-green-400 ring-opacity-50'
                    : 'relative scale-100 shadow-sm'
                }`}
              >
                <div className="text-center">
                  {(() => {
                    const destBankInfo = getDestinationBankInfo();
                    return (
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-1.5 shadow-md overflow-hidden"
                        style={{
                          backgroundColor: destBankInfo?.Color || '#10b981',
                        }}
                      >
                        {destBankInfo?.Image ? (
                          <Img
                            src={destBankInfo.Image}
                            alt={destBankInfo.Type}
                            className="w-6 h-6 object-contain"
                            ratio={1}
                          />
                        ) : (
                          <i className="fas fa-building text-white text-sm"></i>
                        )}
                      </div>
                    );
                  })()}
                  <h6 className="font-bold text-gray-900 mb-1.5 text-xs">{t('ไป')}</h6>

                  {/* Bank Name */}
                  <div className="mb-1.5">
                    <p className="text-gray-600 text-xs mb-0.5">{t('ธนาคาร')}</p>
                    <p className="font-bold text-gray-900 text-xs leading-tight">
                      {pendingDeposit.dest_bank_name ||
                        pendingDeposit.bank_name ||
                        t('ธนาคารปลายทาง')}
                    </p>
                  </div>

                  {/* Account Number */}
                  <div className="mb-1.5">
                    <p className="text-gray-600 text-xs mb-0.5">{t('เลขบัญชี')}</p>
                    <div
                      className={`bg-white/80 backdrop-blur-sm rounded p-1.5 shadow-sm flex items-center justify-between cursor-pointer hover:bg-white/90 transition-all duration-700 group ${
                        showCopyAnimation
                          ? 'transform scale-125 bg-white shadow-lg ring-2 ring-green-300'
                          : ''
                      }`}
                      onClick={handleCopyAccountNumber}
                      title={copySuccess ? t('คัดลอกแล้ว!') : t('กดเพื่อคัดลอกเลขบัญชี')}
                    >
                      <code
                        className={`text-gray-900 font-mono tracking-tight break-all leading-tight flex-1 mr-2 group-hover:text-gray-700 transition-all duration-700 ${
                          showCopyAnimation ? 'text-lg font-bold text-green-800' : 'text-xs'
                        }`}
                      >
                        {pendingDeposit.dest_bank_account ||
                          pendingDeposit.bank_account ||
                          'XXX-X-XXXXX-X'}
                      </code>
                      <div
                        className={`w-5 h-5 rounded flex items-center justify-center transition-all duration-200 flex-shrink-0 ${
                          copySuccess
                            ? 'bg-green-100 text-green-600'
                            : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                        }`}
                      >
                        {copySuccess ? (
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                            <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Account Name */}
                  {pendingDeposit.dest_bank_account_name && (
                    <div className="mb-1.5">
                      <p className="text-gray-600 text-xs mb-0.5">{t('ชื่อบัญชี')}</p>
                      <p className="text-gray-900 text-xs font-medium leading-tight">
                        {pendingDeposit.dest_bank_account_name}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Timer Display - Simple Text */}
            <div className="text-center mt-3 mb-4">
              {timeLeft > 0 ? (
                <div className="inline-flex items-center">
                  <span className="text-gray-600 text-sm mr-2">{t('เวลาที่เหลือ')}:</span>
                  <span
                    className={`font-mono font-bold text-lg ${
                      timeLeft <= 300 ? 'text-red-600 animate-pulse' : 'text-gray-900'
                    }`}
                  >
                    {formatTimeLeft(timeLeft)}
                  </span>
                  <span className="text-gray-500 text-sm ml-1">{t('นาที')}</span>
                </div>
              ) : (
                <div className="text-red-600 font-semibold text-sm">
                  ⚠️ {t('หมดเวลาการฝากเงิน')} - {t('กรุณาทำรายการใหม่')}
                </div>
              )}
            </div>

            {/* Amount Display - Simple Text */}
            <div className="text-center mb-4">
              <div className="flex flex-col items-center">
                <div className="inline-flex items-center">
                  <span className="text-gray-600 text-sm mr-2">{t('จำนวนเงิน')}:</span>
                  <span className="font-bold text-xl text-gray-900">
                    {CurrencyName === 'THB' ? '' : CurrencyName || ''}
                    {exchangeV2 && pendingDeposit.rate_in && pendingDeposit.rate_in > 0
                      ? ((pendingDeposit.amount || 0) / pendingDeposit.rate_in).toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )
                      : (pendingDeposit.net_amount || 0).toLocaleString()}
                  </span>
                </div>
                {exchangeV2 && exchangeV2.rate_in > 0 && (
                  <div className="text-sm font-bold text-green-600 mt-1 bg-green-50 px-3 py-1 rounded-full border border-green-100">
                    {t('ได้รับ')}: {pendingDeposit.net_amount?.toLocaleString()} Credit
                  </div>
                )}
              </div>

              {/* Warning Message */}
              <div className="mt-3 p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl shadow-sm">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="text-left flex-1">
                    <h4 className="text-red-800 text-sm font-semibold mb-2">{t('คำเตือน')}</h4>
                    <p className="text-red-700 text-sm font-bold animate-pulse leading-relaxed">
                      {t('กรุณาโอนเงินยอด')}{' '}
                      <span className="text-xl underline decoration-2 underline-offset-2">
                        {exchangeV2 && pendingDeposit.rate_in && exchangeV2.rate_in > 0
                          ? ((pendingDeposit.amount || 0) / exchangeV2.rate_in).toLocaleString(
                              undefined,
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )
                          : pendingDeposit.amount?.toLocaleString()}
                        {CurrencyName === 'THB' ? '' : ` ${CurrencyName || ''}`}
                      </span>{' '}
                      {t('ให้ถูกต้องทุกทศนิยม (ถ้ามี) มิฉะนั้นระบบจะไม่เติมเครดิตให้ท่านอัตโนมัติ')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Reference ID */}
          <div className="space-y-2 mt-2">
            <label className="block text-gray-700 text-xs font-semibold">
              {t('หมายเลขอ้างอิง')}
            </label>
            <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl border-2 border-gray-300 p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <code className="text-gray-900 font-bold text-xs tracking-wider">
                  {pendingDeposit.reference_id}
                </code>
                <button
                  className="bg-gradient-to-r from-gray-800 to-gray-900 hover:from-black hover:to-gray-800 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-sm"
                  onClick={handleCopyReferenceId}
                >
                  <i className="fas fa-copy mr-1.5"></i>
                  {t('คัดลอก')}
                </button>
              </div>
            </div>
          </div>

          {/* Upload Slip Section */}
          <div className="space-y-2 mt-4">
            {/* Only show upload section if no slip has been uploaded yet */}
            {!uploadedSlipUrl ? (
              <>
                <label className="block text-gray-700 text-xs font-semibold">
                  {t('อัพโหลดสลิปโอนเงิน')} ({t('ไม่จำเป็น')})
                </label>

                {!slipPreview ? (
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleSlipFileChange}
                      className="hidden"
                      id="slip-upload"
                      disabled={uploadingSlip}
                    />
                    <label
                      htmlFor="slip-upload"
                      className="flex flex-col items-center justify-center w-full p-6 border-2 border-dashed border-gray-300 rounded-xl bg-gradient-to-br from-gray-50 to-white hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 cursor-pointer group"
                    >
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2 group-hover:bg-blue-200 transition-all duration-300">
                          <i className="fas fa-cloud-upload-alt text-blue-600 text-xl"></i>
                        </div>
                        <p className="text-gray-700 font-medium text-sm mb-1">
                          {t('คลิกเพื่ออัพโหลดสลิป')}
                        </p>
                        <p className="text-gray-500 text-xs">
                          {t('รองรับไฟล์ JPG, PNG (ไม่เกิน 5MB)')}
                        </p>
                      </div>
                    </label>
                  </div>
                ) : (
                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 border-gray-200 p-3 shadow-sm">
                    {/* Preview Image */}
                    <div className="relative mb-3">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={slipPreview}
                        alt="Slip Preview"
                        className="w-full h-48 object-contain rounded-lg bg-gray-100"
                      />
                      <button
                        onClick={handleRemoveSlip}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-all duration-200 transform hover:scale-110 active:scale-95 shadow-lg"
                        disabled={uploadingSlip}
                      >
                        <i className="fas fa-times text-sm"></i>
                      </button>
                    </div>

                    {/* Upload Button */}
                    <button
                      onClick={handleUploadSlip}
                      disabled={uploadingSlip}
                      className={`w-full py-2.5 px-4 rounded-lg font-bold text-sm transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] ${
                        uploadingSlip
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-md hover:shadow-lg'
                      }`}
                    >
                      {uploadingSlip ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          {t('กำลังอัพโหลด...')}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-upload mr-2"></i>
                          {t('อัพโหลดสลิป')}
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Info Message */}
                <div className="flex items-start space-x-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                  <i className="fas fa-info-circle text-blue-600 text-xs mt-0.5"></i>
                  <p className="text-blue-800 text-xs leading-relaxed">
                    {t('การอัพโหลดสลิปจะช่วยเร่งกระบวนการตรวจสอบและเติมเงินให้รวดเร็วขึ้น')}
                  </p>
                </div>
              </>
            ) : (
              /* Display Uploaded Slip - Only show this section if slip exists */
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl border-2 border-green-200 p-3 shadow-sm">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <i className="fas fa-check text-white text-xs"></i>
                  </div>
                  <h6 className="font-bold text-green-800 text-sm">{t('สลิปที่อัพโหลดแล้ว')}</h6>
                </div>
                <div className="relative rounded-lg overflow-hidden bg-white">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={uploadedSlipUrl}
                    alt="Uploaded Slip"
                    className="w-full h-48 object-contain bg-gray-50"
                  />
                </div>
                <button
                  onClick={() => window.open(uploadedSlipUrl, '_blank')}
                  className="w-full mt-2 py-2 px-3 bg-white hover:bg-gray-50 border border-green-300 rounded-lg text-green-700 font-medium text-xs transition-all duration-200"
                >
                  <i className="fas fa-external-link-alt mr-1.5"></i>
                  {t('ดูสลิปขนาดเต็ม')}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="w-14 h-14 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-md">
            <i className="fas fa-search text-yellow-600 text-lg"></i>
          </div>
          <h6 className="font-bold text-gray-900 text-base mb-1">{t('ไม่พบข้อมูลการฝากเงิน')}</h6>
          <p className="text-gray-600 text-xs leading-relaxed">{t('กรุณาลองใหม่อีกครั้ง')}</p>
        </div>
      )}

      {renderSelectedPromotionInfo()}
    </div>
  );

  const isQrCodeUrl = (qrCode?: string | null): qrCode is string =>
    typeof qrCode === 'string' &&
    (qrCode.startsWith('http://') || qrCode.startsWith('https://'));

  const renderStep3versionQR = () => {
    const qrCode = pendingDeposit?.qr_code;

    // If qr_code is a URL, return iframe directly
    if (isQrCodeUrl(qrCode)) {
      return (
        <iframe
          src={qrCode}
          title="QR Code"
          className="w-full flex-1 border-0 rounded-none sm:rounded-lg"
          style={{ width: '100%', height: '100%', minHeight: isDesktopViewport ? '560px' : '0' }}
          frameBorder="0"
          allowFullScreen
        />
      );
    }

    return (
      <div className="space-y-4">
        {pendingDeposit ? (
          <div>
            {/* Transfer Flow */}
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 border-gray-200 p-3 sm:p-4 shadow-md">
              <div className="text-center mb-3 sm:mb-4">
                <h6 className="font-bold text-gray-900 text-sm sm:text-base mb-1 sm:mb-2">
                  {t('ข้อมูลการโอนเงิน')}
                </h6>
                <div className="w-12 sm:w-16 h-0.5 bg-gradient-to-r from-blue-400 to-blue-600 mx-auto rounded-full"></div>
              </div>

              {/* QR Code Section - Big and Prominent */}
              <div className="mb-6">
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl border-2 border-green-200 p-6 shadow-lg">
                  <div className="text-center">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {t('สแกน QR Code เพื่อโอนเงิน')}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      {t('ใช้แอปธนาคารของคุณสแกน QR Code ด้านล่าง')}
                    </p>

                    {/* Large QR Code Container */}
                    <div className="bg-white rounded-xl p-6 sm:p-8 shadow-inner mb-4">
                      <div className="w-full mx-auto rounded-lg overflow-hidden border-2 border-gray-200">
                        {pendingDeposit.qr_code ? (
                          <div className="w-full aspect-square bg-white mx-auto flex items-center justify-center p-4 sm:p-6">
                            <QRCode
                              size={256}
                              className="w-full h-full max-w-xs max-h-xs"
                              value={pendingDeposit.qr_code ?? ''}
                              viewBox={`0 0 256 256`}
                              style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                            />
                          </div>
                        ) : null}
                      </div>

                      {/* Open in New Tab Button - Only show for URLs */}
                      {isQrCodeUrl(pendingDeposit?.qr_code) && (
                          <div className="mt-4 text-center">
                            <button
                              onClick={() => window.open(pendingDeposit?.qr_code, '_blank')}
                              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-medium rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-md hover:shadow-lg"
                            >
                              <i className="fas fa-external-link-alt mr-2 text-xs"></i>
                              {t('เปิดในแท็บใหม่')}
                            </button>
                          </div>
                        )}
                    </div>

                    {/* QR Code Instructions */}
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                      <div className="flex items-start space-x-2">
                        <i className="fas fa-info-circle text-blue-600 text-sm mt-0.5"></i>
                        <div className="text-left">
                          <p className="text-blue-800 text-sm font-medium mb-1">
                            {t('วิธีการโอนเงิน')}
                          </p>
                          <ul className="text-blue-700 text-xs space-y-1 list-disc list-inside">
                            <li>{t('เปิดแอปธนาคารของคุณ')}</li>
                            <li>{t('เลือกเมนูโอนเงินผ่าน QR Code')}</li>
                            <li>{t('สแกน QR Code ด้านบน')}</li>
                            <li>{t('ตรวจสอบจำนวนเงินและโอนเงิน')}</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bank Information Section */}
              <div className="grid grid-cols-1 gap-3 sm:gap-4 mb-3 sm:mb-4">
                {/* Source Bank (Top) */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border-2 border-blue-200 p-3 sm:p-4 shadow-sm">
                  <div className="text-center">
                    {(() => {
                      // Find source bank info from Banks array using the selected bank ID from pendingDeposit
                      const sourceBankInfo =
                        Banks?.find((bank) => bank.ID === selectedBank?.ID) || selectedBank;
                      return (
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-md overflow-hidden"
                          style={{
                            backgroundColor: sourceBankInfo?.Color || '#3b82f6',
                          }}
                        >
                          {sourceBankInfo?.Image ? (
                            <Img
                              src={sourceBankInfo.Image}
                              alt={sourceBankInfo.Type}
                              className="w-8 h-8 object-contain"
                              ratio={1}
                            />
                          ) : (
                            <i className="fas fa-user text-white text-base"></i>
                          )}
                        </div>
                      );
                    })()}
                    <h6 className="font-bold text-gray-900 mb-2 text-sm">{t('จาก')}</h6>

                    {/* Bank Name */}
                    <div className="mb-2">
                      <p className="text-gray-600 text-sm mb-1">{t('ธนาคาร')}</p>
                      <p className="font-bold text-gray-900 text-sm leading-tight">
                        {selectedBank?.Type || t('ธนาคารของคุณ')}
                      </p>
                    </div>

                    {/* Account Number */}
                    <div className="mb-2">
                      <p className="text-gray-600 text-sm mb-1">{t('เลขบัญชี')}</p>
                      <div className="bg-white/80 backdrop-blur-sm rounded p-2 shadow-sm">
                        <code className="text-gray-900 text-sm font-mono tracking-tight break-all leading-tight">
                          {selectedBank?.BankNumber || 'XXX-X-XXXXX-X'}
                        </code>
                      </div>
                    </div>

                    {/* Account Name */}
                    {selectedBank?.BankName && (
                      <div>
                        <p className="text-gray-600 text-sm mb-1">{t('ชื่อบัญชี')}</p>
                        <p className="text-gray-900 text-sm font-medium leading-tight">
                          {selectedBank.BankName}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Timer Display - Simple Text */}
              <div className="text-center mt-3 mb-4">
                {timeLeft > 0 ? (
                  <div className="inline-flex items-center">
                    <span className="text-gray-600 text-sm mr-2">{t('เวลาที่เหลือ')}:</span>
                    <span
                      className={`font-mono font-bold text-lg ${
                        timeLeft <= 300 ? 'text-red-600 animate-pulse' : 'text-gray-900'
                      }`}
                    >
                      {formatTimeLeft(timeLeft)}
                    </span>
                    <span className="text-gray-500 text-sm ml-1">{t('นาที')}</span>
                  </div>
                ) : (
                  <div className="text-red-600 font-semibold text-sm">
                    ⚠️ {t('หมดเวลาการฝากเงิน')} - {t('กรุณาทำรายการใหม่')}
                  </div>
                )}
              </div>

              {/* Amount Display - Simple Text */}
              <div className="text-center mb-4">
                <div className="inline-flex items-center">
                  <span className="text-gray-600 text-sm mr-2">{t('จำนวนเงิน')}:</span>
                  <span className="font-bold text-xl text-gray-900">
                    {CurrencyName === 'THB' ? '฿' : CurrencyName || '฿'}
                    {pendingDeposit.amount?.toLocaleString()}
                  </span>
                </div>

                {/* Warning Message */}
                <div className="mt-3 p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl shadow-sm">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-red-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="text-left flex-1">
                      <h4 className="text-red-800 text-sm font-semibold mb-2">{t('คำเตือน')}</h4>
                      <p className="text-red-700 text-sm font-bold animate-pulse leading-relaxed">
                        {t('กรุณาโอนเงินยอด')}{' '}
                        <span className="text-xl underline decoration-2 underline-offset-2">
                          {exchangeV2 && exchangeV2.rate_in > 0
                            ? ((pendingDeposit.amount || 0) / exchangeV2.rate_in).toLocaleString(
                                undefined,
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )
                            : pendingDeposit.amount?.toLocaleString()}
                          {CurrencyName === 'THB' ? '' : ` ${CurrencyName || ''}`}
                        </span>{' '}
                        {t(
                          'ให้ถูกต้องทุกทศนิยม (ถ้ามี) มิฉะนั้นระบบจะไม่เติมเครดิตให้ท่านอัตโนมัติ'
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Reference ID */}
            <div className="space-y-2 mt-2">
              <label className="block text-gray-700 text-xs font-semibold">
                {t('หมายเลขอ้างอิง')}
              </label>
              <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl border-2 border-gray-300 p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <code className="text-gray-900 font-bold text-xs tracking-wider">
                    {pendingDeposit.reference_id}
                  </code>
                  <button
                    className="bg-gradient-to-r from-gray-800 to-gray-900 hover:from-black hover:to-gray-800 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-sm"
                    onClick={handleCopyReferenceId}
                  >
                    <i className="fas fa-copy mr-1.5"></i>
                    {t('คัดลอก')}
                  </button>
                </div>
              </div>
            </div>

            {/* Upload Slip Section */}
            <div className="space-y-2 mt-4">
              {/* Only show upload section if no slip has been uploaded yet */}
              {!uploadedSlipUrl ? (
                <>
                  <label className="block text-gray-700 text-xs font-semibold">
                    {t('อัพโหลดสลิปโอนเงิน')} ({t('ไม่จำเป็น')})
                  </label>

                  {!slipPreview ? (
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleSlipFileChange}
                        className="hidden"
                        id="slip-upload-qr"
                        disabled={uploadingSlip}
                      />
                      <label
                        htmlFor="slip-upload-qr"
                        className="flex flex-col items-center justify-center w-full p-6 border-2 border-dashed border-gray-300 rounded-xl bg-gradient-to-br from-gray-50 to-white hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 cursor-pointer group"
                      >
                        <div className="flex flex-col items-center">
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2 group-hover:bg-blue-200 transition-all duration-300">
                            <i className="fas fa-cloud-upload-alt text-blue-600 text-xl"></i>
                          </div>
                          <p className="text-gray-700 font-medium text-sm mb-1">
                            {t('คลิกเพื่ออัพโหลดสลิป')}
                          </p>
                          <p className="text-gray-500 text-xs">
                            {t('รองรับไฟล์ JPG, PNG (ไม่เกิน 5MB)')}
                          </p>
                        </div>
                      </label>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 border-gray-200 p-3 shadow-sm">
                      {/* Preview Image */}
                      <div className="relative mb-3">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={slipPreview}
                          alt="Slip Preview"
                          className="w-full h-48 object-contain rounded-lg bg-gray-100"
                        />
                        <button
                          onClick={handleRemoveSlip}
                          className="absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-all duration-200 transform hover:scale-110 active:scale-95 shadow-lg"
                          disabled={uploadingSlip}
                        >
                          <i className="fas fa-times text-sm"></i>
                        </button>
                      </div>

                      {/* Upload Button */}
                      <button
                        onClick={handleUploadSlip}
                        disabled={uploadingSlip}
                        className={`w-full py-2.5 px-4 rounded-lg font-bold text-sm transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] ${
                          uploadingSlip
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-md hover:shadow-lg'
                        }`}
                      >
                        {uploadingSlip ? (
                          <>
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                            {t('กำลังอัพโหลด...')}
                          </>
                        ) : (
                          <>
                            <i className="fas fa-upload mr-2"></i>
                            {t('อัพโหลดสลิป')}
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Info Message */}
                  <div className="flex items-start space-x-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                    <i className="fas fa-info-circle text-blue-600 text-xs mt-0.5"></i>
                    <p className="text-blue-800 text-xs leading-relaxed">
                      {t('การอัพโหลดสลิปจะช่วยเร่งกระบวนการตรวจสอบและเติมเงินให้รวดเร็วขึ้น')}
                    </p>
                  </div>
                </>
              ) : (
                /* Display Uploaded Slip - Only show this section if slip exists */
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl border-2 border-green-200 p-3 shadow-sm">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <i className="fas fa-check text-white text-xs"></i>
                    </div>
                    <h6 className="font-bold text-green-800 text-sm">{t('สลิปที่อัพโหลดแล้ว')}</h6>
                  </div>
                  <div className="relative rounded-lg overflow-hidden bg-white">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={uploadedSlipUrl}
                      alt="Uploaded Slip"
                      className="w-full h-48 object-contain bg-gray-50"
                    />
                  </div>
                  <button
                    onClick={() => window.open(uploadedSlipUrl, '_blank')}
                    className="w-full mt-2 py-2 px-3 bg-white hover:bg-gray-50 border border-green-300 rounded-lg text-green-700 font-medium text-xs transition-all duration-200"
                  >
                    <i className="fas fa-external-link-alt mr-1.5"></i>
                    {t('ดูสลิปขนาดเต็ม')}
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-14 h-14 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-md">
              <i className="fas fa-search text-yellow-600 text-lg"></i>
            </div>
            <h6 className="font-bold text-gray-900 text-base mb-1">{t('ไม่พบข้อมูลการฝากเงิน')}</h6>
            <p className="text-gray-600 text-xs leading-relaxed">{t('กรุณาลองใหม่อีกครั้ง')}</p>
          </div>
        )}

        {renderSelectedPromotionInfo()}
      </div>
    );
  };

  const renderStep3 = () => {
    const hasQrCode = !!pendingDeposit?.qr_code;
    if (hasQrCode) {
      return renderStep3versionQR();
    } else {
      return renderStep3versionBank();
    }
  };

  const getCurrentStepContent = () => {
    switch (step) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return renderStep1();
    }
  };

  const isIframeQrMode = step === 3 && isQrCodeUrl(pendingDeposit?.qr_code);
  const isStepOneOrTwo = step === 1 || step === 2;

  return (
    <>
      {/* Modal Backdrop */}
      {isDepositV2Modal && (
        <div
          className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-md flex items-start justify-center p-0 overflow-hidden animate-in fade-in duration-300"
          onClick={(e) => {
            // Close modal when clicking backdrop (not the modal content)
            if (e.target === e.currentTarget && !loading) {
              closeModal();
            }
          }}
          style={{
            zIndex: 9999,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          {/* Scrollable Container */}
          <div
            className={`w-full h-full overflow-y-auto flex items-start justify-center ${
              isIframeQrMode
                ? 'p-2 sm:p-4 pt-4 pb-4 sm:pt-4 sm:pb-4'
                : 'p-4 sm:p-6 pt-20 pb-20 sm:pt-12 sm:pb-12'
            }`}
            onClick={(e) => {
              // Also allow clicking in scrollable area to close modal
              if (e.target === e.currentTarget && !loading) {
                closeModal();
              }
            }}
          >
            {/* Modal Container */}
            <div
              className={`relative w-full bg-white shadow-2xl transform transition-all duration-500 ease-out flex flex-col animate-in slide-in-from-bottom-8 zoom-in-95 duration-700 ease-out ${
                isIframeQrMode
                  ? 'max-w-full rounded-2xl sm:rounded-3xl mt-0 sm:mt-0'
                  : 'max-w-md rounded-3xl h-auto max-h-[75vh] sm:max-h-[70vh] mt-4 sm:mt-0'
              } ${
                showCancelConfirm ? 'opacity-20 pointer-events-none' : 'opacity-100'
              }`}
              style={
                isIframeQrMode
                  ? {
                      width: isDesktopViewport ? 'min(64rem, 100%)' : '100%',
                      height: isDesktopViewport ? '96vh' : '100%',
                      maxHeight: isDesktopViewport ? '96vh' : '100%',
                    }
                  : undefined
              }
              onClick={(e) => {
                // Prevent modal from closing when clicking inside the modal
                e.stopPropagation();
              }}
            >
              {/* Close Button - Top Right Corner */}
              {!loading && (
                <button
                  onClick={() => closeModal()}
                  className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white hover:bg-gray-50 text-gray-500 hover:text-gray-700 transition-all duration-200 hover:scale-110 active:scale-95 shadow-md border border-gray-200"
                  title={t('ปิด')}
                >
                  <span className="text-lg font-bold leading-none">×</span>
                </button>
              )}

              {/* Modal Header */}
              <div className="bg-gradient-to-br from-blue-50 to-white p-3 sm:p-6 rounded-t-3xl border-b border-gray-100 flex-shrink-0">
                <div className="animate-in slide-in-from-left-6 duration-600 delay-200">
                  <h3 className="text-base sm:text-xl font-bold text-gray-900">
                    {stepTitles[step]}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {t('ขั้นตอนที่')} {step} {t('จาก')} 3
                  </p>
                </div>
              </div>

              {/* Modal Body */}
              <div
                className={`flex-1 animate-in fade-in slide-in-from-bottom-6 duration-700 delay-300 ${
                  isIframeQrMode ? 'p-0 sm:p-4 min-h-0 flex' : 'sm:p-6'
                } ${
                  isIframeQrMode ? 'overflow-hidden' : 'overflow-y-auto'
                } ${
                  isStepOneOrTwo ? 'px-3 pt-3' : ''
                }`}
              >
                {/* Step Indicator */}
                {!isIframeQrMode && (
                  <div className="flex items-center justify-center mb-3 sm:mb-6">
                    {[1, 2, 3].map((s, index) => (
                      <React.Fragment key={s}>
                        <div
                          className={`w-7 h-7 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm transition-all duration-300 animate-in zoom-in duration-600 ${
                            step >= s
                              ? 'bg-gray-800 text-white shadow-lg'
                              : 'bg-gray-200 text-gray-500'
                          }`}
                          style={{ animationDelay: `${500 + index * 150}ms` }}
                        >
                          {s}
                        </div>
                        {index < 2 && (
                          <div
                            className={`h-0.5 w-8 sm:w-16 mx-2 sm:mx-3 rounded-full transition-all duration-300 animate-in slide-in-from-left duration-600 ${
                              step > s ? 'bg-gray-800' : 'bg-gray-200'
                            }`}
                            style={{ animationDelay: `${550 + index * 150}ms` }}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                )}

                {/* Content */}
                <div
                  className={`animate-in fade-in slide-in-from-bottom-4 duration-600 delay-600 ${
                    isIframeQrMode ? 'h-full min-h-0 flex-1 flex' : ''
                  }`}
                >
                  {getCurrentStepContent()}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-3 sm:p-6 border-t border-gray-100 bg-white rounded-b-3xl flex-shrink-0 animate-in slide-in-from-bottom-6 duration-600 delay-500">
                <button
                  className={`w-full py-2.5 sm:py-4 px-4 sm:px-6 rounded-xl font-bold text-sm sm:text-base transition-all duration-200 ${
                    isButtonDisabled()
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : step === 3
                      ? 'bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]'
                      : 'bg-green-500 hover:bg-black text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]'
                  }`}
                  onClick={handleNext}
                  disabled={isButtonDisabled()}
                >
                  {loading && <i className="fas fa-spinner fa-spin mr-2"></i>}
                  {getButtonText()}
                </button>
                <button
                  className={`w-full mt-3 py-2.5 sm:py-4 px-4 sm:px-6 rounded-xl font-bold text-sm sm:text-base transition-all duration-200 bg-gray-800 text-white`}
                  onClick={() => closeModal()}
                >
                  {t('ปิด')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div
          style={{ zIndex: 99999 }}
          className="fixed inset-0  bg-black/60 backdrop-blur-md flex items-center justify-center p-4"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-300 relative z-[100000]">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⚠️</span>
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-2">{t('ยืนยันการยกเลิก')}</h3>

              <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                {t('คุณต้องการยกเลิกรายการฝากเงินนี้ใช่หรือไม่?')}
                <br />
                <span className="text-red-600 font-medium">
                  {t('การกระทำนี้ไม่สามารถยกเลิกได้')}
                </span>
              </p>

              <div className="grid grid-cols-2 gap-3">
                <button
                  className="py-2.5 px-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition-all duration-200 disabled:opacity-50"
                  onClick={confirmCancelDeposit}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      {t('กำลังยกเลิก...')}
                    </>
                  ) : (
                    t('ยืนยันยกเลิก')
                  )}
                </button>

                <button
                  className="py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all duration-200"
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={loading}
                >
                  {t('ยกเลิก')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const mapStateToProps = (state: AppReducerStateProps) => {
  return {
    state: state,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    closeModal: () => {
      dispatch({
        type: memberActionTypes.ACTION_DEPOSIT_V2_IS_CLOSE_MODAL,
      });

      dispatch({
        type: promotionsActionTypes.ACTION_CLEAR_SELECTED_PROMOTION,
      });
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AlertWithDepositV2);
